import { useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { useSetAtom } from "jotai";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import type { BlockType } from "@/components/Layouts/blockTypes";
import { WorkflowType } from "@/generated";
import {
  MAX_OPEN_BLOCKS,
  openBlock,
  openBlocksByGroupAtom,
  type OpenBlock,
} from "@/store/open-blocks";

// `?openTabs=flows:<id>,assistant:<id>,data-hub:<id>` opens several tools at
// once (a deployed template's building blocks). Its kinds use the old tab
// names.
const OPEN_TABS_TYPE: Record<string, BlockType> = {
  flows: "flow",
  assistant: "assistant",
  "data-hub": "knowledge_base",
  "data-table": "table",
};

// Adds the tools `?openTabs=` names to the open tools, then drops the
// parameter from the address so a reload does not add them again.
export const useOpenTabsParam = () => {
  const searchParams = useSearchParams();
  const { spotGroup } = useSpotUser();
  const setByGroup = useSetAtom(openBlocksByGroupAtom);
  const param = searchParams?.get("openTabs");

  useEffect(() => {
    if (!param) return;
    const refs = param.split(",").flatMap((item) => {
      const [kind, id] = item.split(":");
      const type = OPEN_TABS_TYPE[kind];
      // An id the tool pages would not route to is dropped, not stored.
      return type && id && /^[a-f0-9-]+$/.test(id) ? [{ type, id }] : [];
    });
    setByGroup((prev) => {
      // Added in reverse so the first one named ends up on top.
      const next = [...refs]
        .reverse()
        .reduce(
          (blocks, ref) =>
            blocks.some((b) => b.id === ref.id)
              ? blocks
              : openBlock(blocks, { ...ref, name: "" }),
          prev[spotGroup.id] ?? []
        );
      return { ...prev, [spotGroup.id]: next };
    });
    const url = new URL(window.location.href);
    url.searchParams.delete("openTabs");
    window.history.replaceState(null, "", url.pathname + url.search);
  }, [param, setByGroup, spotGroup.id]);
};

// What the workspace says about the open tools: their current names (one
// renamed elsewhere, or added by `?openTabs=` without one) and the ones it
// no longer returns (deleted, or no longer shared with you). A list that
// failed to load says nothing, so no tool is dropped on an error.
export const useOpenBlockRecords = (
  blocks: OpenBlock[]
): { names: Record<string, string>; gone: Set<string> } => {
  const { spotGroup } = useSpotUser();
  const idsOf = (type: BlockType) =>
    blocks.filter((b) => b.type === type).map((b) => b.id);
  const flowIds = idsOf("flow");
  const agentIds = idsOf("assistant");
  const kbIds = idsOf("knowledge_base");
  const hasTables = blocks.some((b) => b.type === "table");

  const { data: flows } = useRemoteData(
    DATA_KEYS.WORKFLOWS,
    {
      path: { group_id: spotGroup.id },
      query: {
        ids: flowIds.join(","),
        allow_error_handler: true,
        allow_inbox_flows: true,
        size: MAX_OPEN_BLOCKS,
      },
    },
    { enabled: flowIds.length > 0 }
  );
  const { data: agents } = useRemoteData(
    DATA_KEYS.ASSISTANTS,
    {
      path: { group_id: spotGroup.id },
      query: {
        ids: agentIds.join(","),
        include_genie: true,
        size: MAX_OPEN_BLOCKS,
      },
    },
    { enabled: agentIds.length > 0 }
  );
  const { data: kbs } = useRemoteData(
    DATA_KEYS.KNOWLEDGE_BASES_V2,
    {
      path: { group_id: spotGroup.id },
      query: { ids: kbIds.join(","), size: MAX_OPEN_BLOCKS },
    },
    { enabled: kbIds.length > 0 }
  );
  const { data: tables } = useRemoteData(
    DATA_KEYS.TABLES,
    { path: { group_id: spotGroup.id } },
    { enabled: hasTables }
  );

  const lists: [string[], { id: string; name: string }[] | undefined][] = [
    // An error handler is listed as "Error handler", not by its own name.
    [
      flowIds,
      flows?.items.map((flow) =>
        flow.type === WorkflowType.HANDLER
          ? { id: flow.id, name: "Error handler" }
          : flow
      ),
    ],
    [agentIds, agents?.items],
    [kbIds, kbs?.items],
    [hasTables ? idsOf("table") : [], tables],
  ];
  const names: Record<string, string> = {};
  const gone = new Set<string>();
  for (const [ids, items] of lists) {
    if (!ids.length || !items) continue;
    for (const item of items) names[item.id] = item.name;
    for (const id of ids) if (!(id in names)) gone.add(id);
  }
  return { names, gone };
};
